import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, EmptyState, formatDate } from "@/components/pcp";
import { FunnelSimple, X, Eye, Camera, TrashSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Apontamentos() {
  const [apontamentos, setApontamentos] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [porCategoria, setPorCategoria] = useState({});
  const [ordens, setOrdens] = useState([]);
  const [equipes, setEquipes] = useState([]);
  const [contratos, setContratos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const PAGE = 50;

  // Filtros (aceita ?equipe_id=&contrato_id=&os_id=&inicio=&fim=&categoria= vindos do Dashboard)
  const urlQ = useMemo(() => new URLSearchParams(window.location.search), []);
  const [fContrato, setFContrato] = useState(urlQ.get("contrato_id") || "");
  const [fOs, setFOs] = useState(urlQ.get("os_id") || "");
  const [fEquipe, setFEquipe] = useState(urlQ.get("equipe_id") || "");
  const [fInicio, setFInicio] = useState(urlQ.get("inicio") || "");
  const [fFim, setFFim] = useState(urlQ.get("fim") || "");
  const [fCategoria, setFCategoria] = useState(urlQ.get("categoria") || "");
  const [fotoModal, setFotoModal] = useState(null);

  const params = useMemo(() => {
    const p = { limit: PAGE };
    if (fContrato) p.contrato_id = fContrato;
    if (fOs) p.os_id = fOs;
    if (fEquipe) p.equipe_id = fEquipe;
    if (fInicio) p.inicio = fInicio;
    if (fFim) p.fim = fFim;
    if (fCategoria) p.categoria = fCategoria;
    return p;
  }, [fContrato, fOs, fEquipe, fInicio, fFim, fCategoria]);

  useEffect(() => {
    Promise.all([api.get("/ordens"), api.get("/equipes"), api.get("/contratos")]).then(([os, eq, ct]) => {
      setOrdens(os.data); setEquipes(eq.data); setContratos(ct.data);
    });
  }, []);

  const load = async (offset = 0) => {
    if (offset === 0) setLoading(true); else setLoadingMore(true);
    try {
      const { data } = await api.get("/apontamentos/resumo", { params: { ...params, offset } });
      setApontamentos((prev) => (offset === 0 ? data.items : [...prev, ...data.items]));
      setTotal(data.total);
      setHasMore(data.has_more);
      setPorCategoria(data.por_categoria || {});
    } catch {
      toast.error("Erro ao carregar apontamentos");
    } finally {
      setLoading(false); setLoadingMore(false);
    }
  };
  useEffect(() => { load(0); }, [params]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = apontamentos;
  const stats = { total, byCategoria: porCategoria };

  const abrirFotos = async (a) => {
    setFotoModal({ ...a, fotos: null });
    try {
      const { data } = await api.get(`/apontamentos/${a.id}/fotos`);
      setFotoModal({ ...a, fotos: data.fotos });
    } catch {
      toast.error("Erro ao carregar fotos");
      setFotoModal(null);
    }
  };

  const clearFilters = () => {
    setFContrato(""); setFOs(""); setFEquipe(""); setFInicio(""); setFFim(""); setFCategoria("");
  };

  const hasFilter = fContrato || fOs || fEquipe || fInicio || fFim || fCategoria;

  // OS filtradas pelo contrato selecionado (se houver)
  const osOptions = fContrato ? ordens.filter((o) => o.contrato_id === fContrato) : ordens;

  const remove = async (a) => {
    const it = a.item;
    const os = a.os;
    const msg = `Excluir este apontamento?\n\nOS ${os?.numero || "?"} · ${it?.descricao || ""}\nQuantidade: ${a.quantidade} ${it?.unidade || ""}\n\nA quantidade executada do item será DESCONTADA e o avanço da OS recalculado.`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/apontamentos/${a.id}`);
      toast.success("Apontamento excluído — item e avanço da OS atualizados");
      load(0);
    } catch (e) {
      toast.error("Erro ao excluir apontamento");
    }
  };

  return (
    <>
      <PageHeader
        testid="page-apontamentos"
        title="Apontamentos de Produção"
        subtitle={loading ? "Carregando…" : `${filtered.length} de ${total} apontamentos${hasFilter ? " · filtro ativo" : ""}`}
      />
      <div className="page-body">
        {/* Filtros */}
        <div className="pcp-card" style={{ padding: 16, marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <FunnelSimple size={16} weight="bold" />
            <div className="overline" style={{ margin: 0 }}>Filtros</div>
            {hasFilter && (
              <button className="btn btn-ghost btn-sm" onClick={clearFilters} style={{ marginLeft: "auto" }}>
                <X size={12} weight="bold" /> Limpar filtros
              </button>
            )}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
            <div>
              <div className="label">Contrato</div>
              <select className="select" value={fContrato} onChange={(e) => { setFContrato(e.target.value); setFOs(""); }} data-testid="filter-contrato">
                <option value="">— Todos —</option>
                {contratos.map((c) => <option key={c.id} value={c.id}>{c.numero} · {c.contratante}</option>)}
              </select>
            </div>
            <div>
              <div className="label">Ordem de Serviço</div>
              <select className="select" value={fOs} onChange={(e) => setFOs(e.target.value)} data-testid="filter-os">
                <option value="">— Todas —</option>
                {osOptions.map((o) => <option key={o.id} value={o.id}>{o.numero} · {o.contratante}</option>)}
              </select>
            </div>
            <div>
              <div className="label">Equipe</div>
              <select className="select" value={fEquipe} onChange={(e) => setFEquipe(e.target.value)} data-testid="filter-equipe">
                <option value="">— Todas —</option>
                {equipes.map((e) => <option key={e.id} value={e.id}>{e.nome}</option>)}
              </select>
            </div>
            <div>
              <div className="label">De</div>
              <input type="date" className="input" value={fInicio} onChange={(e) => setFInicio(e.target.value)} data-testid="filter-inicio" />
            </div>
            <div>
              <div className="label">Até</div>
              <input type="date" className="input" value={fFim} onChange={(e) => setFFim(e.target.value)} data-testid="filter-fim" />
            </div>
            <div>
              <div className="label">Categoria</div>
              <select className="select" value={fCategoria} onChange={(e) => setFCategoria(e.target.value)} data-testid="filter-categoria">
                <option value="">— Todas —</option>
                {["Horizontal", "Vertical", "Defensas", "Tachas", "Demarcação", "Outros"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Estatísticas do filtro */}
        {stats.total > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 20 }}>
            <div className="pcp-card" style={{ padding: 14 }}>
              <div className="overline">Apontamentos filtrados</div>
              <div className="kpi-value" style={{ fontSize: "1.6rem", marginTop: 4 }}>{stats.total}</div>
            </div>
            {Object.entries(stats.byCategoria).map(([cat, qtd]) => (
              <div key={cat} className="pcp-card" style={{ padding: 14 }}>
                <div className="overline">{cat}</div>
                <div className="kpi-value" style={{ fontSize: "1.6rem", marginTop: 4 }}>{qtd.toLocaleString("pt-BR", { maximumFractionDigits: 2 })}</div>
              </div>
            ))}
          </div>
        )}

        {/* Tabela */}
        {loading ? (
          <div style={{ padding: 32 }}>Carregando…</div>
        ) : filtered.length === 0 ? (
          <EmptyState
            title={hasFilter ? "Nenhum apontamento com esses filtros" : "Nenhum apontamento registrado"}
            description={hasFilter ? "Ajuste os filtros ou limpe-os para ver mais resultados." : "Registre um apontamento em Apontamento → mobile."}
          />
        ) : (
          <div className="pcp-card" style={{ overflowX: "auto" }}>
            <table className="pcp-table" data-testid="apontamentos-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>OS</th>
                  <th>Item</th>
                  <th>Categoria</th>
                  <th style={{ textAlign: "right" }}>Quantidade</th>
                  <th>Equipe</th>
                  <th>Observação</th>
                  <th>Fotos</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((a) => {
                  const os = a.os;
                  const it = a.item;
                  const eq = a.equipe_nome ? { nome: a.equipe_nome } : null;
                  return (
                    <tr key={a.id}>
                      <td className="font-mono text-xs">
                        {formatDate(a.data_execucao)}
                        <div style={{ color: "#71717A" }}>
                          {a.data_execucao ? new Date(a.data_execucao).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : ""}
                        </div>
                      </td>
                      <td className="font-mono font-semibold">
                        {os ? (
                          <Link to={`/ordens/${os.id}`} style={{ color: "#0A0A0A", textDecoration: "none" }}>
                            {os.numero}
                          </Link>
                        ) : "—"}
                        <div style={{ fontSize: "0.7rem", color: "#71717A", fontFamily: "'IBM Plex Sans', sans-serif" }}>{os?.contratante}</div>
                      </td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{it?.descricao || "—"}</div>
                        {it && <div style={{ fontSize: "0.7rem", color: "#71717A" }}>{[it.material, it.cor].filter(Boolean).join(" · ")}</div>}
                      </td>
                      <td>{it && <span className="chip">{it.categoria}</span>}</td>
                      <td className="font-mono font-semibold" style={{ textAlign: "right" }}>
                        {a.quantidade} {it?.unidade}
                      </td>
                      <td>{eq?.nome || "—"}</td>
                      <td style={{ fontSize: "0.8rem", color: "#52525B", maxWidth: 260 }}>
                        {a.pintura?.trecho_descricao ? (
                          <>
                            <div style={{ color: "#0A0A0A", fontWeight: 600 }}>{a.pintura.trecho_descricao}</div>
                            {a.observacao ? <div style={{ marginTop: 2 }}>{a.observacao}</div> : null}
                          </>
                        ) : (a.observacao || "—")}
                      </td>
                      <td>
                        {a.fotos_count > 0 ? (
                          <button className="btn btn-ghost btn-sm" onClick={() => abrirFotos(a)} title="Ver fotos" data-testid={`btn-fotos-${a.id}`}>
                            <Camera size={14} weight="bold" /> {a.fotos_count}
                          </button>
                        ) : (
                          <span style={{ color: "#71717A", fontSize: "0.75rem" }}>—</span>
                        )}
                      </td>
                      <td>
                        {os && (
                          <Link to={`/ordens/${os.id}`} className="btn btn-ghost btn-sm" title="Abrir OS">
                            <Eye size={14} weight="bold" />
                          </Link>
                        )}
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => remove(a)}
                          title="Excluir apontamento"
                          data-testid={`btn-del-apontamento-${a.id}`}
                        >
                          <TrashSimple size={14} weight="bold" color="#DC2626" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {hasMore && (
              <div style={{ padding: 14, textAlign: "center", borderTop: "1px solid #E4E4E7" }}>
                <button className="btn btn-outline" onClick={() => load(apontamentos.length)} disabled={loadingMore} data-testid="btn-carregar-mais">
                  {loadingMore ? "Carregando…" : `Carregar mais (${total - apontamentos.length} restantes)`}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal de fotos */}
      {fotoModal && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(10,10,10,0.85)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
          onClick={() => setFotoModal(null)}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#FFF", padding: 20, maxWidth: 900, maxHeight: "90vh", overflow: "auto", borderRadius: 4 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 className="font-display font-bold">Fotos do apontamento — {formatDate(fotoModal.data_execucao)}</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setFotoModal(null)}>Fechar</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
              {fotoModal.fotos === null ? (
                <div style={{ padding: 24, color: "#71717A" }}>Carregando fotos…</div>
              ) : fotoModal.fotos.map((f, i) => (
                <img key={i} src={f} alt={`Foto ${i + 1}`} style={{ width: "100%", borderRadius: 4, border: "1px solid #E4E4E7" }} />
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
